import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import {
  contractors, orgs,
  type Contractor, type ContractorCreate, type ContractorType,
  type ContractorTaxSystem, type ContractorRoleFilter,
  type ContractRow, type ContractorContact,
} from '@/api/catalogs';
import { refs } from '@/api/refs';
import { projects } from '@/api/projects';
import { ContractsTab, ContactsTab, BankTab } from '@/pages/ContractorRelatedTabs';
import { quarantine } from '@/api/quarantine';
import { api } from '@/api/client';
import Drawer from '@/components/Drawer';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { isDirty } from '@/utils/dirty';
import { useConfirm } from '@/components/ConfirmProvider';
import {
  findBankByIban, type BankRow,
} from '@/shared/ukr_classifiers';

const EMPTY: ContractorCreate = {
  code: null,
  name: '',
  full_name: null,
  contractor_type: 'LEGAL',
  edrpou: null,
  inn: null,
  is_vat_payer: false,
  vat_number: null,
  tax_system: null,
  legal_address: null,
  actual_address: null,
  phone: null,
  email: null,
  is_buyer: false,
  is_supplier: false,
  is_other: false,
  // TD-NONRES + TD-GROUPS B (2026-06-06).
  is_non_resident: false,
  group_id: null,
  // MIGRATION-EXT.2 (2026-06-05): КВЕД.
  kved_code: null,
};

// ===== Норма #20 v2 (2026-06-02): live validation (дзеркало backend helpers) =====

// Рішення власника 2026-06-02: лише формат (без checksum).
// Прецедент: ЄДРПОУ 34927739, 40075815 (Розетка), 39119549 (Нова Пошта) —
// не пройшли б стандартний алгоритм Держстату, хоча реальні. Краще
// приймати raw і покладатись на сторонні джерела (opendatabot, ДПС API).
function isValidEdrpou(v: string): boolean {
  return /^\d{8}$/.test(v);
}

function isValidRnokpp(v: string): boolean {
  return /^\d{10}$/.test(v);
}

function isValidVatNumber(v: string): boolean {
  return /^\d{9,12}$/.test(v);
}

// contractor_type → дозволені tax_system варіанти.
const TAX_SYSTEMS_BY_TYPE: Record<ContractorType, ContractorTaxSystem[]> = {
  LEGAL: ['GENERAL', 'SIMPLIFIED_3', 'SIMPLIFIED_4'],
  INDIVIDUAL_FOP: ['FOP_GENERAL', 'FOP_1', 'FOP_2', 'FOP_3_3', 'FOP_3_5'],
  INDIVIDUAL: [],
  SEPARATE_DIVISION: [],
};

const TAX_SYSTEM_LABELS: Record<ContractorTaxSystem, string> = {
  GENERAL: 'Загальна (податок на прибуток)',
  SIMPLIFIED_3: 'Спрощена 3 група (5% / 3% з ПДВ)',
  SIMPLIFIED_4: 'Спрощена 4 група (с/г)',
  FOP_GENERAL: 'ФОП на загальній (ст. 177 ПКУ)',
  FOP_1: 'ФОП 1 група (єдиний, без ПДВ)',
  FOP_2: 'ФОП 2 група (єдиний, без ПДВ)',
  FOP_3_3: 'ФОП 3 група 3% (з ПДВ)',
  FOP_3_5: 'ФОП 3 група 5% (без ПДВ)',
};

// Чи фіксовано is_vat_payer системою оподаткування.
// FOP_1/2/3_5 — нi (закон забороняє).
// FOP_3_3 — так (закон вимагає).
// Iнші — null (бухгалтер обирає сам, бо залежить від обороту/виду діяльності).
function vatPayerFixedByTaxSystem(ts: ContractorTaxSystem | null): boolean | null {
  if (ts === 'FOP_1' || ts === 'FOP_2' || ts === 'FOP_3_5') return false;
  if (ts === 'FOP_3_3') return true;
  return null;
}

export default function ContractorsPage() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');  // '' = всі групи
  const [roleFilter, setRoleFilter] = useState<'' | ContractorRoleFilter>('');
  const [editing, setEditing] = useState<Contractor | 'new' | null>(null);
  const [form, setForm] = useState<ContractorCreate>(EMPTY);
  // Вкладки пов'язаних сутностей у картці (як в Організації).
  const [relTab, setRelTab] = useState<'contracts' | 'bank' | 'contacts'>('contracts');

  // TD-PAGINATION (2026-06-07): серверна пагінація через ag-Grid Infinite.
  const gridApiRef = useRef<GridApi<Contractor> | null>(null);
  // Збереження ширин колонок між перезавантаженнями (дзеркало DataTable).
  const colW = useGridColumnWidths('contractors');
  const { datasource, total } = useInfiniteGridDatasource<Contractor>(
    (limit, offset, sort) => contractors.list({
      q: search || undefined, group_id: groupFilter || undefined,
      role: roleFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, groupFilter, roleFilter],
  );
  const refreshGrid = () => {
    gridApiRef.current?.refreshInfiniteCache();
    // Бейджі карантину можуть змінитись після правки → оновити.
    qc.invalidateQueries({ queryKey: ['quarantine'] });
  };
  // TD-GROUPS B (2026-06-06): групи контрагентів для dropdown у картці.
  const groupsQ = useQuery({
    queryKey: ['contractor-groups'],
    queryFn: refs.contractorGroups.list,
  });

  // MIGRATION-EXT.1 (2026-06-05): пов'язані сутності для read-only display
  // у формі редагування. Тільки коли editing — існуючий контрагент.
  const editingId = editing && editing !== 'new' ? editing.id : null;
  const contractsQ = useQuery({
    queryKey: ['contractor-contracts', editingId],
    queryFn: () =>
      api.get('/refs/contracts', { params: { contractor_id: editingId } })
        .then((r) => r.data as Array<{
          id: string; code: string; name: string;
          contract_date: string | null; contract_type: string;
          // TD-CONTRACT-FIELDS (2026-06-06): розширені поля договору.
          civil_kind_id: string | null; number: string | null;
          valid_until: string | null; is_foreign_economic: boolean;
        }>),
    enabled: editingId !== null,
  });
  // Види договорів по ЦК — для відображення назви у таблиці договорів картки.
  const civilKindsQ = useQuery({
    queryKey: ['contract-civil-kinds'],
    queryFn: refs.contractCivilKinds.list,
  });
  // Для форм створення договорів: юрособи (organization_id) + валюти.
  const orgsListQ = useQuery({
    queryKey: ['orgs-for-contract'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  // Проекти — для поля «Проект за замовч.» у договорі (alembic 0252).
  const projectsForContractQ = useQuery({
    queryKey: ['projects-for-contract'],
    queryFn: () => projects.list({ limit: 200 }),
    enabled: editingId !== null,
  });
  const refreshContracts = () => qc.invalidateQueries({ queryKey: ['contractor-contracts', editingId] });
  const refreshContacts = () => qc.invalidateQueries({ queryKey: ['contractor-contacts', editingId] });
  const refreshBanks = () => qc.invalidateQueries({ queryKey: ['contractor-bank-accounts', editingId] });
  const bankAccountsQ = useQuery({
    queryKey: ['contractor-bank-accounts', editingId],
    queryFn: () =>
      refs.contractorBankAccounts(editingId || undefined),
    enabled: editingId !== null,
  });
  const contactsQ = useQuery({
    queryKey: ['contractor-contacts', editingId],
    queryFn: () =>
      api.get('/refs/contractor-contacts', { params: { contractor_id: editingId } })
        .then((r) => r.data as Array<{ id: string; name: string; position: string | null; phone: string | null; email: string | null; birthday: string | null; is_main: boolean }>),
    enabled: editingId !== null,
  });
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks() as Promise<BankRow[]>,
    staleTime: 1000 * 60 * 60,
  });
  // Migration Quarantine: issues для редагованого карантинного контрагента.
  const editingNeedsReview =
    editing && editing !== 'new' ? editing.needs_review : false;
  const quarantineQ = useQuery({
    queryKey: ['contractor-quarantine', editingId],
    queryFn: () =>
      quarantine.contractors({ include_resolved: false, limit: 500 }),
    enabled: editingId !== null && editingNeedsReview,
  });
  const quarantineIssues = useMemo(() => {
    if (!editingId || !quarantineQ.data) return [];
    const rec = quarantineQ.data.items.find((q) => q.entity_id === editingId);
    return rec?.issues ?? [];
  }, [editingId, quarantineQ.data]);
  const create = useMutation({
    mutationFn: (p: ContractorCreate) => contractors.create(p),
    onSuccess: () => { refreshGrid(); setEditing(null); },
  });
  const update = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<ContractorCreate> }) => contractors.update(id, p),
    onSuccess: () => { refreshGrid(); setEditing(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractors.remove(id),
    onSuccess: () => refreshGrid(),
  });

  const cols = useMemo<ColDef<Contractor>[]>(() => [
    {
      headerName: '',
      width: 110,
      sortable: false,
      // Значок «І» імпорту з 1С + Migration Quarantine badge (потребує виправлення).
      cellRenderer: (p: { data?: Contractor }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ImportedBadge source={p.data?.external_source} />
          {p.data?.needs_review ? (
            <span
              title="Реквізити з 1С не відповідають стандарту. Відкрийте картку → виправте."
              style={{
                background: '#fde68a', color: '#92400e', borderRadius: 4,
                padding: '2px 6px', fontSize: 12, fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              ⚠ Карантин
            </span>
          ) : null}
        </span>
      ),
    },
    { field: 'code', headerName: t('contractor.code'), width: 110, sortable: true },
    { field: 'name', headerName: t('contractor.name'), flex: 1, minWidth: 250, sortable: true },
    {
      field: 'contractor_type',
      headerName: t('contractor.type'),
      width: 170, sortable: true,
      valueFormatter: (p) => t(`contractor.types.${p.value as ContractorType}`),
    },
    { field: 'edrpou', headerName: t('contractor.edrpou'), width: 120, sortable: true },
    { field: 'inn', headerName: t('contractor.inn'), width: 120, sortable: true },
    {
      field: 'is_vat_payer',
      headerName: t('contractor.is_vat_payer'),
      width: 110, sortable: true,
      valueFormatter: (p) => (p.value ? t('common.yes') : t('common.no')),
    },
    {
      headerName: 'Роль',
      width: 200,
      valueGetter: (p) => {
        const c = p.data;
        if (!c) return '';
        const tags = [];
        if (c.is_buyer) tags.push('Покупець');
        if (c.is_supplier) tags.push('Постачальник');
        if (c.is_other) tags.push('Iнше');
        return tags.join(', ');
      },
    },
  ], [t]);

  function startCreate() { setForm(EMPTY); setEditing('new'); }
  function startEdit(c: Contractor) {
    setForm({
      code: c.code, name: c.name, full_name: c.full_name,
      contractor_type: c.contractor_type,
      edrpou: c.edrpou, inn: c.inn,
      is_vat_payer: c.is_vat_payer, vat_number: c.vat_number,
      tax_system: c.tax_system,
      legal_address: c.legal_address,
      actual_address: c.actual_address,
      phone: c.phone, email: c.email,
      is_buyer: c.is_buyer, is_supplier: c.is_supplier, is_other: c.is_other,
      is_non_resident: c.is_non_resident, group_id: c.group_id,
      kved_code: c.kved_code,
    });
    setEditing(c);
  }
  function save() {
    if (editing === 'new') create.mutate(form);
    else if (editing) update.mutate({ id: editing.id, p: form });
  }

  // ===== Live validation marks =====
  const edrpouValid = form.edrpou ? isValidEdrpou(form.edrpou) : null;
  const innValid = form.inn ? isValidRnokpp(form.inn) : null;
  const vatNumValid = form.vat_number ? isValidVatNumber(form.vat_number) : null;

  const isFop = form.contractor_type === 'INDIVIDUAL_FOP';
  const isLegal = form.contractor_type === 'LEGAL';
  const isIndividual = form.contractor_type === 'INDIVIDUAL';
  const isSeparateDivision = form.contractor_type === 'SEPARATE_DIVISION';
  // ЕДРПОУ — лише юрособа / відокремлений підрозділ; ФОП/фізособа → РНОКПП.
  // Нерезидент не має укр. коду → ховаємо обидва поля.
  const showEdrpou = (isLegal || isSeparateDivision) && !form.is_non_resident;
  const showRnokpp = (isFop || isIndividual) && !form.is_non_resident;

  const availableTaxSystems = TAX_SYSTEMS_BY_TYPE[form.contractor_type];
  // Нерезидент: укр. податкові реквізити не застосовуються → ховаємо
  // систему оподаткування / ПДВ / КВЕД (правка картки 2026-06-07).
  const showTaxSystem = availableTaxSystems.length > 0 && !form.is_non_resident;
  const showVatBlock = !isIndividual && !form.is_non_resident;
  const showKved = !form.is_non_resident;

  // Pre-flight save validation — щоб користувач НЕ натискав Save з невалідною формою.
  const formErrors: string[] = [];
  if (!form.name.trim()) formErrors.push('Назва обов\'язкова');
  // Нерезидент не має укр. ЄДРПОУ/РНОКПП → не вимагаємо (TD-NONRES).
  if (isLegal && !form.is_non_resident && !form.edrpou) formErrors.push('ЄДРПОУ обов\'язковий для юр.особи');
  if (isLegal && form.edrpou && !edrpouValid) formErrors.push('ЄДРПОУ — невірна контрольна сума');
  if (isFop && !form.is_non_resident && !form.inn) formErrors.push('РНОКПП обов\'язковий для ФОП');
  if (isFop && form.inn && !innValid) formErrors.push('РНОКПП — невірна контрольна сума');
  if (form.vat_number && !vatNumValid) formErrors.push('ПДВ-номер — 9-12 цифр');
  if (form.is_vat_payer && !form.vat_number) formErrors.push('Якщо платник ПДВ — потрібен номер свідоцтва');
  if (isIndividual && form.is_vat_payer) formErrors.push('Фіз.особа не може бути платником ПДВ');
  if (!form.is_buyer && !form.is_supplier && !form.is_other && !form.is_non_resident) {
    formErrors.push('Вкажіть хоча б одну роль');
  }
  const canSave = formErrors.length === 0;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {t('contractor.title')}
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
              ({total})
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Фільтр по ролі контрагента (2026-06-07). */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as '' | ContractorRoleFilter)}
          >
            <option value="">Усі ролі</option>
            <option value="buyer">Покупець</option>
            <option value="supplier">Постачальник</option>
            <option value="non_resident">Нерезидент</option>
            <option value="other">Iнше</option>
          </select>
          {/* Фільтр по групі контрагента (TD-GROUPS B, 2026-06-07). */}
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
            <option value="">Усі групи</option>
            {(groupsQ.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <a href="/refs/contractor-groups" style={{ fontSize: 13, alignSelf: 'center' }}>
            Керувати групами →
          </a>
          <button className="primary" onClick={startCreate}>+ {t('contractor.new')}</button>
        </div>
      </div>
      <div className="ag-theme-quartz grid">
        <AgGridReact<Contractor>
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={100}
          columnDefs={cols}
          onGridReady={(e: GridReadyEvent<Contractor>) => { gridApiRef.current = e.api; colW.applyTo(e.api); }}
          onColumnResized={colW.onColumnResized}
          onRowDoubleClicked={(e) => e.data && startEdit(e.data)}
          rowSelection="single"
          getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        />
      </div>

      <Drawer
        open={editing !== null}
        width="wide"
        title={editing === 'new' ? t('contractor.new') : (editing as Contractor | null)?.name ?? ''}
        onClose={() => setEditing(null)}
        dirty={editing !== null && isDirty(form, editing === 'new' ? null : editing as unknown as Partial<ContractorCreate>)}
        footer={
          <>
            {editing !== 'new' && editing && (
              <button
                className="danger"
                onClick={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) { remove.mutate(editing.id); setEditing(null); } }) }}
              >
                {t('common.delete')}
              </button>
            )}
            <button onClick={() => setEditing(null)}>{t('common.cancel')}</button>
            <button
              className="primary"
              onClick={save}
              disabled={!canSave || create.isPending || update.isPending}
              title={canSave ? undefined : formErrors.join('; ')}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        {/* Migration Quarantine: банер з переліком що виправити. */}
        {editingNeedsReview && (
          <div
            style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem',
            }}
          >
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
              ⚠ Контрагент у карантині міграції
            </div>
            <div style={{ fontSize: 13, color: '#78350f', marginBottom: 8 }}>
              Реквізити з 1С не відповідають стандарту. Виправте поля нижче —
              після збереження контрагент стане доступним у документах.
            </div>
            {quarantineIssues.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#78350f' }}>
                {quarantineIssues.map((iss, idx) => (
                  <li key={idx}>{iss.message_uk}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Code + type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="field">
            <label>
              {t('contractor.code')}{' '}
              <small style={{ color: 'var(--muted)' }}>
                (auto: CTR-1, якщо порожнє)
              </small>
            </label>
            <input
              value={form.code ?? ''}
              onChange={(e) => setForm({ ...form, code: e.target.value || null })}
              placeholder="CTR-1 (auto)"
            />
          </div>
          <div className="field">
            <label>{t('contractor.type')}</label>
            <select
              value={form.contractor_type}
              onChange={(e) => {
                const newType = e.target.value as ContractorType;
                // При зміні типу — скидаємо tax_system та edrpou/inn якщо вони не релевантні.
                setForm({
                  ...form,
                  contractor_type: newType,
                  tax_system: null,
                  is_vat_payer: newType === 'INDIVIDUAL' ? false : form.is_vat_payer,
                });
              }}
            >
              <option value="LEGAL">{t('contractor.types.LEGAL')}</option>
              <option value="INDIVIDUAL_FOP">{t('contractor.types.INDIVIDUAL_FOP')}</option>
              <option value="INDIVIDUAL">{t('contractor.types.INDIVIDUAL')}</option>
              <option value="SEPARATE_DIVISION">{t('contractor.types.SEPARATE_DIVISION')}</option>
            </select>
          </div>
        </div>

        {/* Name */}
        <div className="field">
          <label>{t('contractor.name')} <span style={{ color: 'red' }}>*</span></label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="field">
          <label>{t('contractor.full_name')}</label>
          <input value={form.full_name ?? ''} onChange={(e) => setForm({ ...form, full_name: e.target.value || null })} />
        </div>

        {/* Roles */}
        <div style={{ marginTop: '0.75rem' }}>
          <strong>Роль <span style={{ color: 'red' }}>*</span></strong>
          <small style={{ color: 'var(--muted)', marginLeft: 6 }}>(хоча б одна)</small>
        </div>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_buyer}
              onChange={(e) => setForm({ ...form, is_buyer: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Покупець
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.is_supplier}
              onChange={(e) => setForm({ ...form, is_supplier: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Постачальник
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.is_other}
              onChange={(e) => setForm({ ...form, is_other: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Iнше
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.is_non_resident}
              onChange={(e) => setForm({ ...form, is_non_resident: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Нерезидент
          </label>
        </div>

        {/* EDRPOU / INN — conditional per type. ФОП→РНОКПП (НЕ ЄДРПОУ);
            нерезидент→ховаємо обидва (немає укр. коду). */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {showEdrpou && (
            <div className="field">
              <label>
                {t('contractor.edrpou')}
                {isLegal && <span style={{ color: 'red' }}> *</span>}
                {form.edrpou && (
                  <span style={{ marginLeft: 6, color: edrpouValid ? 'green' : 'red', fontWeight: 'bold' }}>
                    {edrpouValid ? '✓' : '✗'}
                  </span>
                )}
              </label>
              <input
                value={form.edrpou ?? ''}
                onChange={(e) => setForm({ ...form, edrpou: e.target.value || null })}
                placeholder="8 цифр"
                maxLength={8}
                style={form.edrpou && !edrpouValid ? { borderColor: 'red' } : undefined}
              />
              {form.edrpou && !edrpouValid && (
                <small style={{ color: 'red' }}>Невірна контрольна сума ЄДРПОУ</small>
              )}
            </div>
          )}
          {showRnokpp && (
            <div className="field">
              <label>
                РНОКПП
                {isFop && <span style={{ color: 'red' }}> *</span>}
                {form.inn && (
                  <span style={{ marginLeft: 6, color: innValid ? 'green' : 'red', fontWeight: 'bold' }}>
                    {innValid ? '✓' : '✗'}
                  </span>
                )}
              </label>
              <input
                value={form.inn ?? ''}
                onChange={(e) => setForm({ ...form, inn: e.target.value || null })}
                placeholder="10 цифр"
                maxLength={10}
                style={form.inn && !innValid ? { borderColor: 'red' } : undefined}
              />
              {form.inn && !innValid && (
                <small style={{ color: 'red' }}>Невірна контрольна сума РНОКПП</small>
              )}
            </div>
          )}
        </div>

        {/* Tax system (для LEGAL/FOP) */}
        {showTaxSystem && (
          <div className="field">
            <label>Система оподаткування</label>
            <select
              value={form.tax_system ?? ''}
              onChange={(e) => {
                const ts = (e.target.value || null) as ContractorTaxSystem | null;
                const vatFixed = vatPayerFixedByTaxSystem(ts);
                setForm({
                  ...form,
                  tax_system: ts,
                  is_vat_payer: vatFixed !== null ? vatFixed : form.is_vat_payer,
                });
              }}
            >
              <option value="">— Не вказано —</option>
              {availableTaxSystems.map((ts) => (
                <option key={ts} value={ts}>
                  {TAX_SYSTEM_LABELS[ts]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* VAT payer + number */}
        {showVatBlock && (
          <div style={{ marginTop: '0.5rem' }}>
            <label>
              <input
                type="checkbox"
                checked={form.is_vat_payer}
                disabled={vatPayerFixedByTaxSystem(form.tax_system ?? null) !== null}
                onChange={(e) => setForm({ ...form, is_vat_payer: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.5rem' }}
              />
              {t('contractor.is_vat_payer')}
              {vatPayerFixedByTaxSystem(form.tax_system ?? null) !== null && (
                <small style={{ color: 'var(--muted)', marginLeft: 6 }}>
                  (фіксовано системою оподаткування)
                </small>
              )}
            </label>
            {form.is_vat_payer && (
              <div className="field" style={{ marginTop: '0.5rem' }}>
                <label>
                  Свідоцтво ПДВ (номер)
                  <span style={{ color: 'red' }}> *</span>
                  {form.vat_number && (
                    <span style={{ marginLeft: 6, color: vatNumValid ? 'green' : 'red', fontWeight: 'bold' }}>
                      {vatNumValid ? '✓' : '✗'}
                    </span>
                  )}
                </label>
                <input
                  value={form.vat_number ?? ''}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value || null })}
                  placeholder="9-12 цифр"
                  maxLength={12}
                  style={form.vat_number && !vatNumValid ? { borderColor: 'red' } : undefined}
                />
              </div>
            )}
          </div>
        )}

        {/* Contacts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div className="field">
            <label>Телефон</label>
            <input
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value || null })}
              placeholder="+38 (0XX) XXX-XX-XX"
            />
          </div>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value || null })}
            />
          </div>
        </div>

        {/* Addresses — галка «фактична співпадає» (як в Організації). */}
        <div className="field">
          <label>Юридична адреса</label>
          <input
            value={form.legal_address ?? ''}
            onChange={(e) => setForm({ ...form, legal_address: e.target.value || null })}
          />
        </div>
        <div className="field" style={{ marginTop: '0.5rem' }}>
          <label>
            <input
              type="checkbox"
              checked={form.actual_address === null || form.actual_address === form.legal_address}
              onChange={(e) => setForm({
                ...form,
                actual_address: e.target.checked ? form.legal_address : '',
              })}
              style={{ width: 'auto', marginRight: '0.5rem' }}
            />
            Фактична адреса співпадає з юридичною
          </label>
        </div>
        {form.actual_address !== null && form.actual_address !== form.legal_address && (
          <div className="field">
            <label>Фактична адреса</label>
            <input
              value={form.actual_address ?? ''}
              onChange={(e) => setForm({ ...form, actual_address: e.target.value || null })}
            />
          </div>
        )}

        {/* MIGRATION-EXT.2 (2026-06-05): КВЕД (ховаємо у нерезидента). */}
        {showKved && (
          <div className="field">
            <label>
              Вид діяльності за КВЕД
              <small style={{ color: 'var(--muted)' }}> (опціонально, формат XX.XX)</small>
            </label>
            <input
              value={form.kved_code ?? ''}
              placeholder="62.01"
              maxLength={7}
              onChange={(e) => setForm({ ...form, kved_code: e.target.value || null })}
            />
          </div>
        )}

        {/* TD-GROUPS B: група контрагента (довідник) */}
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          Група
          <select
            value={form.group_id ?? ''}
            onChange={(e) => setForm({ ...form, group_id: e.target.value || null })}
            style={{ display: 'block', marginTop: 4, minWidth: 240 }}
          >
            <option value="">— без групи —</option>
            {(groupsQ.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </label>

        {/* Errors summary */}
        {formErrors.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.5rem',
            backgroundColor: 'rgba(255, 0, 0, 0.08)',
            borderLeft: '3px solid red',
            fontSize: '0.85em',
          }}>
            <strong>Виправте перед збереженням:</strong>
            <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
              {formErrors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        )}

        {/* MIGRATION-EXT.1 (2026-06-05): read-only пов'язані сутності.
            Тільки у режимі редагування існуючого контрагента. */}
        {editing !== 'new' && editing !== null && (
          <div style={{ marginTop: '1rem' }}>
            {/* Вкладки пов'язаних сутностей (як в Організації). */}
            <div className="seg-tabs">
              <button type="button" className={relTab === 'contracts' ? 'active' : ''}
                onClick={() => setRelTab('contracts')}>
                Договори ({contractsQ.data?.length ?? 0})
              </button>
              <button type="button" className={relTab === 'bank' ? 'active' : ''}
                onClick={() => setRelTab('bank')}>
                Банк-рахунки ({bankAccountsQ.data?.length ?? 0})
              </button>
              <button type="button" className={relTab === 'contacts' ? 'active' : ''}
                onClick={() => setRelTab('contacts')}>
                Контактні особи ({contactsQ.data?.length ?? 0})
              </button>
            </div>

            {relTab === 'contracts' && (
              <ContractsTab
                contractorId={editing.id}
                contracts={(contractsQ.data ?? []) as ContractRow[]}
                orgs={(orgsListQ.data?.items ?? []).map((o) => ({ id: o.id, name: o.name, is_vat_payer: o.is_vat_payer, use_returnable_packaging: o.use_returnable_packaging }))}
                currencies={(currenciesQ.data ?? []).map((c) => ({ id: c.id, code: c.code }))}
                civilKinds={(civilKindsQ.data ?? []).map((k) => ({ id: k.id, name: k.name }))}
                projects={(projectsForContractQ.data?.items ?? []).map((p) => ({ id: p.id, name: p.name }))}
                onChanged={refreshContracts}
              />
            )}
            {relTab === 'bank' && (
              <BankTab
                contractorId={editing.id}
                accounts={(bankAccountsQ.data ?? []).map((ba) => ({
                  id: ba.id, owner_id: editing.id, iban: ba.iban,
                  currency_id: '', account_name: ba.account_name ?? null, is_main: ba.is_main,
                }))}
                currencies={(currenciesQ.data ?? []).map((c) => ({ id: c.id, code: c.code }))}
                bankNameByIban={(iban) => findBankByIban(iban, banksQ.data ?? [])}
                onChanged={refreshBanks}
              />
            )}
            {relTab === 'contacts' && (
              <ContactsTab
                contractorId={editing.id}
                contacts={(contactsQ.data ?? []) as ContractorContact[]}
                onChanged={refreshContacts}
              />
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}
